// eslint-disable-next-line no-restricted-syntax -- This repo uses the classic JSX transform.
import * as React from 'react';
import { useState } from 'react';
import ListSubheader from '@mui/material/ListSubheader';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useRestartAnalyticsOnboarding } from '@internal/backstage-plugin-reporting';
import { LogoutIcon } from './icons';

type UserMenuProps = {
  anchorEl: HTMLElement | null;
  userLabel: string;
  onClose: () => void;
  onLogout: () => void;
};

const paperSx = {
  minWidth: 220,
  borderRadius: 2,
  border: 1,
  borderColor: 'divider',
  '& .MuiMenuItem-root': { fontSize: 14 },
};

export function UserMenu({
  anchorEl,
  userLabel,
  onClose,
  onLogout,
}: UserMenuProps) {
  const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);
  const { restart, isRestarting } = useRestartAnalyticsOnboarding();

  const closeAll = () => {
    setHelpAnchor(null);
    onClose();
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeAll}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: paperSx }}
        MenuListProps={{ 'aria-label': 'User menu' }}
        disableEnforceFocus={Boolean(helpAnchor)}
      >
        <ListSubheader
          disableSticky
          sx={{
            px: 2,
            py: 1,
            fontSize: 14,
            lineHeight: 1.5,
            color: 'text.secondary',
            bgcolor: 'transparent',
          }}
        >
          {userLabel}
        </ListSubheader>
        <Divider />
        <MenuItem
          aria-haspopup="menu"
          aria-expanded={Boolean(helpAnchor)}
          aria-controls={helpAnchor ? 'user-help-menu' : undefined}
          onClick={event => setHelpAnchor(event.currentTarget)}
          onKeyDown={event => {
            if (event.key === 'ArrowRight') {
              event.preventDefault();
              setHelpAnchor(event.currentTarget);
            }
          }}
        >
          <ListItemIcon>
            <HelpOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText disableTypography>Help</ListItemText>
          <ChevronRightIcon fontSize="small" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeAll();
            onLogout();
          }}
        >
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={helpAnchor}
        open={Boolean(anchorEl && helpAnchor)}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') closeAll();
          else setHelpAnchor(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: paperSx }}
        MenuListProps={{ id: 'user-help-menu', 'aria-label': 'Help' }}
        onKeyDown={event => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            setHelpAnchor(null);
          }
        }}
      >
        <MenuItem
          disabled={isRestarting}
          onClick={() => {
            closeAll();
            restart();
          }}
        >
          Analytics tips
        </MenuItem>
      </Menu>
    </>
  );
}
